import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import axios from 'axios';
import { API_BASE_URL } from '../api/client';
import './Vera.css';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
);

const SUGGESTED_QUESTIONS = [
  'What data do we have cached right now?',
  'How many referrals were there in the last 3 months by facility?',
  'Show me a chart of messages by type over the last 6 months',
  'Which facilities have the most VitalRounds?',
  'Compare login counts across all facilities',
  'What are the top 5 facilities by referral volume?',
];

const CHART_COLORS = [
  'rgba(20, 184, 166, 0.8)',
  'rgba(245, 87, 108, 0.8)',
  'rgba(16, 185, 129, 0.8)',
  'rgba(245, 158, 11, 0.8)',
  'rgba(139, 92, 246, 0.8)',
  'rgba(236, 72, 153, 0.8)',
  'rgba(54, 162, 235, 0.8)',
  'rgba(255, 206, 86, 0.8)',
  'rgba(75, 192, 192, 0.8)',
  'rgba(153, 102, 255, 0.8)',
  'rgba(255, 159, 64, 0.8)',
  'rgba(199, 199, 199, 0.8)',
];

function loadSession(key, fallback) {
  try {
    const stored = sessionStorage.getItem(`vera_${key}`);
    return stored ? JSON.parse(stored) : fallback;
  } catch { return fallback; }
}

function saveSession(key, value) {
  try { sessionStorage.setItem(`vera_${key}`, JSON.stringify(value)); } catch {}
}

function Vera() {
  const [messages, setMessages] = useState(() => loadSession('messages', []));
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [visualizations, setVisualizations] = useState(() => loadSession('visualizations', []));
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(() => loadSession('provider', ''));
  const [selectedModel, setSelectedModel] = useState(() => loadSession('model', ''));
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { saveSession('messages', messages); }, [messages]);
  useEffect(() => { saveSession('visualizations', visualizations); }, [visualizations]);
  useEffect(() => { if (selectedProvider) saveSession('provider', selectedProvider); }, [selectedProvider]);
  useEffect(() => { if (selectedModel) saveSession('model', selectedModel); }, [selectedModel]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    inputRef.current?.focus();
    loadProviders();
  }, []);

  const loadProviders = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/vera/providers`);
      const list = res.data.providers || [];
      setProviders(list);
      if (list.length > 0) {
        const savedProvider = loadSession('provider', '');
        const savedModel = loadSession('model', '');
        const hasSavedProvider = savedProvider && list.some(p => p.id === savedProvider);
        if (hasSavedProvider) {
          setSelectedProvider(savedProvider);
          const prov = list.find(p => p.id === savedProvider);
          const hasModel = savedModel && prov?.models?.some(m => m.id === savedModel);
          setSelectedModel(hasModel ? savedModel : (prov?.models?.find(m => m.default) || prov?.models?.[0])?.id || '');
        } else {
          setSelectedProvider(list[0].id);
          const defaultModel = list[0].models?.find(m => m.default) || list[0].models?.[0];
          setSelectedModel(defaultModel?.id || '');
        }
      }
    } catch {
      setProviders([]);
    }
  };

  const handleProviderChange = (providerId) => {
    setSelectedProvider(providerId);
    const prov = providers.find(p => p.id === providerId);
    const defaultModel = prov?.models?.find(m => m.default) || prov?.models?.[0];
    setSelectedModel(defaultModel?.id || '');
  };

  const currentProviderModels = useMemo(() => {
    return providers.find(p => p.id === selectedProvider)?.models || [];
  }, [providers, selectedProvider]);

  const conversationHistory = useMemo(() => {
    return messages.map(m => ({ role: m.role, content: m.content }));
  }, [messages]);

  const sendMessage = async (text) => {
    const userMessage = text || input.trim();
    if (!userMessage || loading) return;

    setInput('');
    const userMsg = { role: 'user', content: userMessage };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const history = [...conversationHistory, { role: 'user', content: userMessage }];
      const response = await axios.post(`${API_BASE_URL}/api/vera/chat`, {
        messages: history,
        provider: selectedProvider,
        model: selectedModel
      }, { timeout: 120000 });

      const { reply, visualizations: vizList } = response.data;

      const provLabel = providers.find(p => p.id === selectedProvider)?.name || selectedProvider;
      const modelLabel = currentProviderModels.find(m => m.id === selectedModel)?.name || selectedModel;
      const assistantMsg = {
        role: 'assistant',
        content: reply || 'Done.',
        vizCount: vizList?.length || 0,
        modelTag: `${provLabel} · ${modelLabel}`
      };
      setMessages(prev => [...prev, assistantMsg]);

      if (vizList && vizList.length > 0) {
        setVisualizations(vizList);
      }
    } catch (err) {
      const errorText = err.response?.data?.error || err.message || 'Failed to reach VERA';
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${errorText}`, isError: true }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setVisualizations([]);
    saveSession('messages', []);
    saveSession('visualizations', []);
  };

  return (
    <div className="vera-page">
      <div className="vera-chat-panel">
        <div className="vera-chat-header">
          <div className="vera-logo">
            <span className="vera-logo-icon">V</span>
            <div>
              <h2>VERA</h2>
              <span className="vera-subtitle">VitalEngine Research Assistant</span>
            </div>
          </div>
          <div className="vera-header-controls">
            {providers.length > 0 && (
              <div className="vera-model-selector">
                <select
                  value={selectedProvider}
                  onChange={e => handleProviderChange(e.target.value)}
                  className="vera-select"
                  disabled={loading}
                >
                  {providers.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <select
                  value={selectedModel}
                  onChange={e => setSelectedModel(e.target.value)}
                  className="vera-select"
                  disabled={loading}
                >
                  {currentProviderModels.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
            )}
            {providers.length === 0 && (
              <span className="vera-no-providers">No AI keys configured</span>
            )}
            {messages.length > 0 && (
              <button className="vera-clear-btn" onClick={clearChat}>Clear</button>
            )}
          </div>
        </div>

        <div className="vera-messages">
          {messages.length === 0 && !loading && (
            <div className="vera-welcome">
              <div className="vera-welcome-icon">V</div>
              <h3>What can I help you analyze?</h3>
              <p>I can query your cached VitalEngine data, create charts, and answer questions about referrals, messages, logins, and more.</p>
              <div className="vera-phi-notice">
                <span className="vera-phi-shield">&#x1F6E1;</span>
                <span>All patient data is <strong>anonymized</strong> before being sent to the AI model. No PHI leaves your system.</span>
              </div>
              <div className="vera-suggestions">
                {SUGGESTED_QUESTIONS.map((q, i) => (
                  <button key={i} className="vera-suggestion" onClick={() => sendMessage(q)}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`vera-msg vera-msg-${msg.role} ${msg.isError ? 'vera-msg-error' : ''}`}>
              <div className="vera-msg-avatar">
                {msg.role === 'user' ? 'You' : 'V'}
              </div>
              <div className="vera-msg-content">
                <MessageContent text={msg.content} />
                <div className="vera-msg-meta">
                  {msg.vizCount > 0 && (
                    <span className="vera-viz-badge">
                      {msg.vizCount} visualization{msg.vizCount !== 1 ? 's' : ''} created →
                    </span>
                  )}
                  {msg.modelTag && (
                    <span className="vera-model-tag">{msg.modelTag}</span>
                  )}
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="vera-msg vera-msg-assistant">
              <div className="vera-msg-avatar">V</div>
              <div className="vera-msg-content">
                <div className="vera-typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        <div className="vera-input-area">
          <div className="vera-input-row">
            <textarea
              ref={inputRef}
              className="vera-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask VERA about your data..."
              rows={1}
              disabled={loading}
            />
            <button
              className="vera-send-btn"
              onClick={() => sendMessage()}
              disabled={loading || !input.trim()}
            >
              Send
            </button>
          </div>
          <div className="vera-phi-bar">
            <span className="vera-phi-dot"></span>
            PHI-Safe: anonymized data only · Do not enter patient identifiers
          </div>
        </div>
      </div>

      <div className="vera-viz-panel">
        <div className="vera-viz-header">
          <h3>Visualizations</h3>
          {visualizations.length > 0 && (
            <span className="vera-viz-count">{visualizations.length} item{visualizations.length !== 1 ? 's' : ''}</span>
          )}
        </div>
        <div className="vera-viz-content">
          {visualizations.length === 0 ? (
            <div className="vera-viz-empty">
              <p>Charts and tables will appear here when VERA creates them.</p>
            </div>
          ) : (
            visualizations.map((viz, i) => (
              <div key={i} className="vera-viz-card">
                {viz.type === 'chart' && <VizChart viz={viz} index={i} />}
                {viz.type === 'table' && <VizTable viz={viz} />}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function MessageContent({ text }) {
  if (!text) return null;

  const lines = text.split('\n');
  const elements = [];
  let inCodeBlock = false;
  let codeLines = [];
  let key = 0;

  for (const line of lines) {
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        elements.push(<pre key={key++} className="vera-code-block">{codeLines.join('\n')}</pre>);
        codeLines = [];
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      codeLines.push(line);
      continue;
    }

    if (line.startsWith('### ')) {
      elements.push(<h4 key={key++}>{line.slice(4)}</h4>);
    } else if (line.startsWith('## ')) {
      elements.push(<h3 key={key++}>{line.slice(3)}</h3>);
    } else if (line.startsWith('# ')) {
      elements.push(<h2 key={key++}>{line.slice(2)}</h2>);
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      elements.push(<li key={key++}>{formatInline(line.slice(2))}</li>);
    } else if (line.trim() === '') {
      elements.push(<br key={key++} />);
    } else {
      elements.push(<p key={key++}>{formatInline(line)}</p>);
    }
  }

  if (inCodeBlock && codeLines.length > 0) {
    elements.push(<pre key={key++} className="vera-code-block">{codeLines.join('\n')}</pre>);
  }

  return <div className="vera-msg-text">{elements}</div>;
}

function formatInline(text) {
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const codeMatch = remaining.match(/`([^`]+)`/);

    let firstMatch = null;
    let matchType = null;

    if (boldMatch && (!codeMatch || boldMatch.index <= codeMatch.index)) {
      firstMatch = boldMatch;
      matchType = 'bold';
    } else if (codeMatch) {
      firstMatch = codeMatch;
      matchType = 'code';
    }

    if (!firstMatch) {
      parts.push(remaining);
      break;
    }

    if (firstMatch.index > 0) {
      parts.push(remaining.slice(0, firstMatch.index));
    }

    if (matchType === 'bold') {
      parts.push(<strong key={key++}>{firstMatch[1]}</strong>);
    } else {
      parts.push(<code key={key++} className="vera-inline-code">{firstMatch[1]}</code>);
    }

    remaining = remaining.slice(firstMatch.index + firstMatch[0].length);
  }

  return parts;
}

function VizChart({ viz, index }) {
  const isPieType = viz.chartType === 'pie' || viz.chartType === 'doughnut';
  const chartData = {
    labels: viz.labels || [],
    datasets: (viz.datasets || []).map((ds, i) => ({
      label: ds.label || `Series ${i + 1}`,
      data: ds.data || [],
      backgroundColor: isPieType
        ? CHART_COLORS.slice(0, (ds.data || []).length)
        : CHART_COLORS[i % CHART_COLORS.length],
      borderColor: isPieType
        ? CHART_COLORS.slice(0, (ds.data || []).length).map(c => c.replace('0.8', '1'))
        : CHART_COLORS[i % CHART_COLORS.length].replace('0.8', '1'),
      borderWidth: viz.chartType === 'line' ? 2.5 : 2,
      tension: 0.3,
      fill: false,
      pointRadius: viz.chartType === 'line' ? 3 : undefined,
      pointHoverRadius: viz.chartType === 'line' ? 5 : undefined,
    }))
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: viz.title || 'Chart', font: { size: 14, weight: 'bold' } }
    },
    scales: viz.chartType === 'pie' || viz.chartType === 'doughnut' ? {} : {
      y: { beginAtZero: true, ticks: { precision: 0 } }
    }
  };

  const ChartComponent = {
    bar: Bar,
    line: Line,
    pie: Pie,
    doughnut: Doughnut,
  }[viz.chartType] || Bar;

  return (
    <div className="vera-chart-wrapper">
      <ChartComponent data={chartData} options={options} />
    </div>
  );
}

function VizTable({ viz }) {
  return (
    <div className="vera-table-wrapper">
      <h4 className="vera-table-title">{viz.title || 'Data'}</h4>
      <table className="vera-table">
        <thead>
          <tr>
            {(viz.columns || []).map((col, i) => (
              <th key={i}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(viz.rows || []).map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci}>{cell === null || cell === undefined ? '—' : String(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default Vera;
